import { useCallback, useEffect, useState } from 'react';

/** Préférence de thème : `auto` suit le système, sinon clair ou sombre forcé (jetons de theme.css). */
export type PreferenceTheme = 'auto' | 'clair' | 'sombre';

const CLE_STOCKAGE = 'theme-preference';
const SUIVANT: Record<PreferenceTheme, PreferenceTheme> = { auto: 'clair', clair: 'sombre', sombre: 'auto' };

export function lirePreference(): PreferenceTheme {
  try {
    const v = window.localStorage.getItem(CLE_STOCKAGE);
    return v === 'clair' || v === 'sombre' ? v : 'auto';
  } catch {
    return 'auto';
  }
}

function ecrirePreference(p: PreferenceTheme): void {
  try {
    if (p === 'auto') window.localStorage.removeItem(CLE_STOCKAGE);
    else window.localStorage.setItem(CLE_STOCKAGE, p);
  } catch {
    // Stockage indisponible (navigation privée) : la préférence vaut pour la session seulement.
  }
}

function appliquer(p: PreferenceTheme): void {
  if (p === 'auto') delete document.documentElement.dataset.theme;
  else document.documentElement.dataset.theme = p;
}

/** Applique la préférence mémorisée dès le démarrage, avant le premier rendu (évite un flash de thème). */
export function appliquerPreferenceStockee(): void {
  appliquer(lirePreference());
}

function systemeSombre(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** Thème effectif (`clair` ou `sombre`) pour une préférence donnée. */
export function themeEffectif(preference: PreferenceTheme, sombreSysteme: boolean): 'clair' | 'sombre' {
  if (preference === 'auto') return sombreSysteme ? 'sombre' : 'clair';
  return preference;
}

export const LIBELLES_THEME: Record<PreferenceTheme, string> = {
  auto: 'Thème automatique',
  clair: 'Thème clair',
  sombre: 'Thème sombre',
};

/** Bascule du thème : applique `data-theme` sur la racine et mémorise le choix si possible. */
export function useTheme() {
  const [preference, setPreference] = useState<PreferenceTheme>(lirePreference);
  const [sombreSysteme, setSombreSysteme] = useState<boolean>(systemeSombre);

  useEffect(() => {
    appliquer(preference);
  }, [preference]);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const surChangement = () => setSombreSysteme(media.matches);
    media.addEventListener('change', surChangement);
    return () => media.removeEventListener('change', surChangement);
  }, []);

  const basculer = useCallback(() => {
    setPreference((courante) => {
      const suivante = SUIVANT[courante];
      ecrirePreference(suivante);
      return suivante;
    });
  }, []);

  return { preference, effectif: themeEffectif(preference, sombreSysteme), basculer };
}
